import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Rainbow, Heart, HandHeart, MessageCircle, ShieldCheck, Pin, Plus, Clock,
  Trash2, Flag, Ban, Send, ChevronDown,
} from "lucide-react";
import {
  fetchPosts, reactToPost, deletePost, resolveImage,
  addComment, deleteComment, reportTarget, blockUser,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CreatePostModal from "@/components/CreatePostModal";

const STICKERS = [
  { key: "rainbow", icon: Rainbow, label: "Lovely", color: "#0284C7" },
  { key: "heart", icon: Heart, label: "Love", color: "#FF6B6B" },
  { key: "pray", icon: HandHeart, label: "Praying", color: "#9333EA" },
];

const REPORT_REASONS = ["Inappropriate content", "Safety concern", "Identifying info about a child", "Spam", "Other"];

const CommentSection = ({ post, user, openAuth, onPostsChange }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const isMine = user && post.author_user_id === user.user_id;
  const commentsAllowed = post.comments_enabled !== false || isMine;

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    if (!user) {
      openAuth("signup");
      return;
    }
    setBusy(true);
    try {
      const comment = await addComment(post.post_id, text.trim());
      onPostsChange((ps) =>
        ps.map((p) => (p.post_id === post.post_id ? { ...p, comments: [...p.comments, comment] } : p))
      );
      setText("");
    } catch {} finally {
      setBusy(false);
    }
  };

  const removeComment = async (commentId) => {
    await deleteComment(post.post_id, commentId).catch(() => {});
    onPostsChange((ps) =>
      ps.map((p) =>
        p.post_id === post.post_id
          ? { ...p, comments: p.comments.filter((c) => c.comment_id !== commentId) }
          : p
      )
    );
  };

  return (
    <div className="mt-4">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`comments-toggle-${post.post_id}`}
        className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0284C7] hover:underline"
      >
        <MessageCircle className="h-4 w-4" />
        {post.comments.length > 0 ? `Comments (${post.comments.length})` : "Comments"}
        <motion.span animate={{ rotate: open ? 180 : 0 }}>
          <ChevronDown className="h-3.5 w-3.5" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="mt-3 space-y-2.5 rounded-2xl bg-[#F8F5EC] p-4" data-testid={`comments-list-${post.post_id}`}>
              {post.comments.length === 0 && (
                <p className="text-sm text-slate-400">Be the first to encourage them.</p>
              )}
              {post.comments.map((c, j) => {
                const canDelete =
                  user &&
                  (c.author_user_id === user.user_id || isMine || user.role === "admin");
                return (
                  <div key={c.comment_id || j} className="flex items-start gap-2.5" data-testid={`comment-${post.post_id}-${j}`}>
                    <span
                      className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-bold text-white"
                      style={{ backgroundColor: c.avatar_color || "#94A3B8" }}
                    >
                      {c.author[0]}
                    </span>
                    <p className="min-w-0 flex-1 text-sm text-slate-600 leading-snug">
                      <span className="font-bold text-slate-800">{c.author}</span> {c.text}
                    </p>
                    {canDelete && c.comment_id && (
                      <button
                        onClick={() => removeComment(c.comment_id)}
                        data-testid={`comment-delete-${c.comment_id}`}
                        aria-label="Delete comment"
                        className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-slate-300 hover:bg-[#FFE5E5] hover:text-[#DC2626]"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                );
              })}
              {commentsAllowed ? (
                <form onSubmit={submit} className="flex gap-2 pt-1">
                  <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    maxLength={500}
                    data-testid={`comment-input-${post.post_id}`}
                    placeholder={user ? "Add a kind word…" : "Sign in to comment"}
                    className="min-w-0 flex-1 rounded-full border border-[#E2DDD0] bg-white px-4 py-2.5 text-sm outline-none focus:border-[#FFC107]"
                  />
                  <button
                    type="submit"
                    disabled={busy || !text.trim()}
                    data-testid={`comment-submit-${post.post_id}`}
                    aria-label="Post comment"
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-slate-900 text-white transition-transform hover:scale-105 disabled:opacity-40"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </form>
              ) : (
                <p className="text-xs font-semibold text-slate-400" data-testid={`comments-off-${post.post_id}`}>
                  Comments are turned off for this post.
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const PostSafetyMenu = ({ post, user, onBlocked }) => {
  const [open, setOpen] = useState(false);
  const [reported, setReported] = useState(false);
  const [reason, setReason] = useState("");

  const report = async () => {
    if (!reason) return;
    await reportTarget({
      target_type: "post",
      target_id: post.post_id,
      target_label: `${post.author}'s post`,
      reason,
    }).catch(() => {});
    setReported(true);
  };

  const block = async () => {
    if (!post.author_user_id) return;
    await blockUser(post.author_user_id).catch(() => {});
    onBlocked(post.author_user_id);
    setOpen(false);
  };

  return (
    <div className="relative ml-auto shrink-0">
      <button
        onClick={() => { setOpen((v) => !v); setReported(false); setReason(""); }}
        data-testid={`safety-menu-${post.post_id}`}
        aria-label="Post safety options"
        className="grid h-8 w-8 place-items-center rounded-full text-slate-300 transition-colors hover:bg-[#F8F5EC] hover:text-slate-500"
      >
        <Flag className="h-4 w-4" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            className="absolute right-0 z-20 mt-1 w-64 rounded-2xl border border-[#E2DDD0] bg-white p-3 shadow-xl"
            data-testid={`safety-dropdown-${post.post_id}`}
          >
            {reported ? (
              <p className="px-2 py-3 text-sm font-semibold text-[#16A34A]" data-testid={`report-thanks-${post.post_id}`}>
                Thank you — our moderation team will review this.
              </p>
            ) : (
              <>
                <p className="px-2 pb-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                  Report this post
                </p>
                <div className="flex flex-wrap gap-1.5 px-2 pb-2">
                  {REPORT_REASONS.map((r) => (
                    <button
                      key={r}
                      onClick={() => setReason(r)}
                      data-testid={`report-reason-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={`rounded-full border px-2.5 py-1 text-xs font-bold transition-colors ${
                        reason === r
                          ? "border-[#FF6B6B] bg-[#FFE5E5] text-[#DC2626]"
                          : "border-[#E2DDD0] text-slate-500 hover:border-slate-400"
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
                <button
                  onClick={report}
                  disabled={!reason}
                  data-testid={`report-submit-${post.post_id}`}
                  className="mt-1 w-full rounded-full bg-[#FF6B6B] py-2 text-sm font-bold text-white disabled:opacity-40"
                >
                  Send report
                </button>
                {post.author_user_id && (
                  <button
                    onClick={block}
                    data-testid={`block-author-${post.post_id}`}
                    className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-full border border-[#E2DDD0] py-2 text-sm font-bold text-slate-600 hover:bg-[#F8F5EC]"
                  >
                    <Ban className="h-4 w-4" /> Block {post.author}
                  </button>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Community = () => {
  const [posts, setPosts] = useState([]);
  const [reacted, setReacted] = useState({});
  const [composerOpen, setComposerOpen] = useState(false);
  const { user, setUser, openAuth } = useAuth();

  useEffect(() => {
    fetchPosts().then(setPosts).catch(() => {});
  }, []);

  const react = async (postId, sticker) => {
    const key = `${postId}:${sticker}`;
    if (reacted[key]) return;
    setReacted((r) => ({ ...r, [key]: true }));
    try {
      const updated = await reactToPost(postId, sticker);
      setPosts((ps) => ps.map((p) => (p.post_id === postId ? updated : p)));
    } catch {
      setReacted((r) => ({ ...r, [key]: false }));
    }
  };

  const openComposer = () => {
    if (!user) {
      openAuth("signup");
      return;
    }
    setComposerOpen(true);
  };

  const removePost = async (postId) => {
    await deletePost(postId);
    setPosts((ps) => ps.filter((p) => p.post_id !== postId));
  };

  const onBlocked = (blockedUserId) => {
    setPosts((ps) => ps.filter((p) => p.author_user_id !== blockedUserId));
    if (user) {
      setUser({ ...user, blocked: [...(user.blocked || []), blockedUserId] });
    }
  };

  return (
    <section id="community" className="relative flex min-h-[100svh] flex-col justify-center bg-[#F8F5EC] py-28 sm:py-36" data-testid="community-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-xs font-bold uppercase tracking-[0.3em] text-[#0284C7]"
            >
              Community
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.08 }}
              className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl"
            >
              Growing faith together.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.16 }}
              className="mt-4 text-base text-slate-600 leading-relaxed sm:text-lg"
            >
              A parent-first space to share crafts, prayer requests and encouragement — never a
              child-to-child social network.
            </motion.p>
          </div>
          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={openComposer}
            data-testid="community-create-post-btn"
            className="inline-flex items-center gap-2 self-start rounded-full bg-slate-900 px-6 py-3.5 font-display text-base font-semibold text-white shadow-lg md:self-auto"
          >
            <Plus className="h-5 w-5" /> Create Post
          </motion.button>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="community-safety-badges">
          {["Adult accounts only", "Moderated posts & photos", "No private messaging", "Report & block built in"].map(
            (b) => (
              <span
                key={b}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#E2DDD0] bg-white px-3.5 py-1.5 text-xs font-bold text-slate-600"
              >
                <ShieldCheck className="h-3.5 w-3.5 text-[#16A34A]" />
                {b}
              </span>
            )
          )}
        </div>

        <div className="mt-12 columns-1 gap-5 md:columns-2 lg:columns-3 [&>*]:mb-5">
          {posts.map((post, i) => {
            const isPending = post.status === "pending";
            const isMine = user && post.author_user_id === user.user_id;
            const canDelete = isMine || user?.role === "admin";
            return (
              <motion.article
                key={post.post_id}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                className={`break-inside-avoid overflow-hidden rounded-3xl border bg-white shadow-[0_10px_30px_-5px_rgba(234,179,8,0.1)] transition-shadow hover:shadow-[0_14px_28px_rgba(0,0,0,0.06)] ${
                  isPending ? "border-dashed border-[#FCD34D]" : "border-[#E2DDD0]"
                }`}
                data-testid={`community-post-${post.post_id}`}
              >
                {post.image && (
                  <div className="relative">
                    <img
                      src={resolveImage(post.image)}
                      alt={`Shared by ${post.author}`}
                      className="h-52 w-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-3">
                    <span
                      className="grid h-11 w-11 shrink-0 place-items-center rounded-full font-display text-base font-bold text-white"
                      style={{ backgroundColor: post.avatar_color }}
                    >
                      {post.author[0]}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate font-display text-base font-semibold text-slate-900">{post.author}</p>
                      <p className="truncate text-xs font-semibold text-slate-400">
                        {post.family} &bull; {post.time}
                      </p>
                    </div>
                    {post.pinned && <Pin className="ml-auto h-4 w-4 shrink-0 rotate-45 text-[#FFC107]" />}
                    {canDelete ? (
                      <button
                        onClick={() => removePost(post.post_id)}
                        data-testid={`community-delete-${post.post_id}`}
                        aria-label="Delete post"
                        className="ml-auto grid h-8 w-8 shrink-0 place-items-center rounded-full text-slate-300 transition-colors hover:bg-[#FFE5E5] hover:text-[#DC2626]"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    ) : (
                      user && <PostSafetyMenu post={post} user={user} onBlocked={onBlocked} />
                    )}
                  </div>
                  {isPending && (
                    <p
                      className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#FEF3C7] px-3 py-1 text-xs font-bold text-[#B45309]"
                      data-testid={`pending-badge-${post.post_id}`}
                    >
                      <Clock className="h-3 w-3" /> Under review — only you can see this
                    </p>
                  )}
                  <p className="mt-4 text-base text-slate-700 leading-relaxed">{post.text}</p>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {STICKERS.map((s) => {
                      const did = reacted[`${post.post_id}:${s.key}`];
                      return (
                        <motion.button
                          key={s.key}
                          whileTap={{ scale: 0.85 }}
                          onClick={() => react(post.post_id, s.key)}
                          data-testid={`react-${post.post_id}-${s.key}`}
                          aria-label={`${s.label} reaction`}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-bold transition-all ${
                            did
                              ? "border-transparent text-white"
                              : "border-[#E2DDD0] bg-[#F8F5EC] text-slate-600 hover:border-slate-300"
                          }`}
                          style={did ? { backgroundColor: s.color } : {}}
                        >
                          <s.icon className="h-4 w-4" />
                          {post.reactions[s.key]}
                        </motion.button>
                      );
                    })}
                  </div>

                  <CommentSection post={post} user={user} openAuth={openAuth} onPostsChange={setPosts} />
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
      <CreatePostModal
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        onCreated={(post) => setPosts((ps) => [post, ...ps])}
      />
    </section>
  );
};

export default Community;
